import { useMediaQuery } from '@mui/material';
import { Button, Logo, MaintenanceBanner, NavBar, Stack } from '@si/components';
import { useRuntimeConfig, useUser } from '@si/hooks';
import { DATETIME_PREFERENCES_EVENT, useTranslate } from '@si/utils';
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useSearchParams } from 'react-router';

import { HeaderProvider } from './components/NavBar/HeaderProvider';
import { LegacyAdminRedirect } from './features/admin/AdminOnly';
import { hasUnreadLearningContent, LearningCenter } from './features/learning-center';
import { useSalesDemoMode } from './features/sales-demo/salesDemoMode';
import { useOnboardingBootstrap } from './hooks/useOnboardingBootstrap';
import { useAuth } from './useAuth';

const Projects = lazy(() => import('./features/projects').then((m) => ({ default: m.Projects })));
const Project = lazy(() => import('./features/projects').then((m) => ({ default: m.Project })));
const AdminPanel = lazy(() =>
  import('./features/admin/AdminPanel').then((m) => ({ default: m.AdminPanel }))
);
const Profile = lazy(() => import('./features/profile').then((m) => ({ default: m.Profile })));
const ReportPdf = lazy(() =>
  import('./features/projects/Project/ReportPdf/ReportPdf').then((m) => ({
    default: m.ReportPdf,
  }))
);
const ReportPrint = lazy(() =>
  import('./features/projects/Project/ReportPrint/ReportPrint').then((m) => ({
    default: m.ReportPrint,
  }))
);
const InstallationPlanPdf = lazy(() =>
  import('./features/projects/Project/ProjectDocumentation/InstallationPlanPdf').then((m) => ({
    default: m.InstallationPlanPdf,
  }))
);

function AppRoutes({
  protectedElement,
  paramLanguage,
}: {
  protectedElement: (element: ReactNode) => ReactNode;
  paramLanguage: string;
}) {
  const { user } = useUser();
  const salesMode = useSalesDemoMode();
  const { i18n } = useTranslation();

  useEffect(() => {
    const preferredLanguage = user?.preferredLanguage;
    if (!paramLanguage && preferredLanguage) i18n.changeLanguage(preferredLanguage);
  }, [i18n, paramLanguage, user?.preferredLanguage]);

  return (
    <Routes>
      <Route path="/" element={protectedElement(<Navigate to="projects" replace />)} />
      <Route path="projects" element={protectedElement(<Projects />)} />
      <Route path="projects/:projectId/*" element={protectedElement(<Project />)} />
      <Route
        path="profile"
        element={protectedElement(salesMode ? <Navigate to="/projects" replace /> : <Profile />)}
      />
      <Route
        path="admin/*"
        element={protectedElement(salesMode ? <Navigate to="/projects" replace /> : <AdminPanel />)}
      />
      <Route
        path="companies"
        element={protectedElement(<LegacyAdminRedirect path="companies" />)}
      />
      <Route path="devices" element={protectedElement(<LegacyAdminRedirect path="devices" />)} />
      <Route path="users" element={protectedElement(<LegacyAdminRedirect path="users" />)} />
      <Route
        path="distributors"
        element={protectedElement(<LegacyAdminRedirect path="distributors" />)}
      />
      <Route path="*" element={protectedElement(<Navigate to="/projects" replace />)} />
    </Routes>
  );
}

export function App() {
  const { authenticated, isAuthenticating, shouldShowLoginCta, login, token } = useAuth();
  const { user } = useUser();
  const salesMode = useSalesDemoMode();
  const { isBootstrapping } = useOnboardingBootstrap({
    authenticated: authenticated && !salesMode,
    isAuthenticating,
    token,
  });
  const { runtimeConfig } = useRuntimeConfig();
  const { i18n } = useTranslation();
  const { t, translate } = useTranslate();
  const isCompactWidth = useMediaQuery('(max-width:1200px)');
  const [, setDatetimePreferencesVersion] = useState(0);
  const [isLearningCenterOpen, setIsLearningCenterOpen] = useState(false);
  const [hasUnreadLearning, setHasUnreadLearning] = useState(() =>
    hasUnreadLearningContent(user?.userId)
  );

  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  const paramLanguage = searchParams.get('language') ?? '';
  const contentPx = isCompactWidth ? '2rem' : '6.5rem';
  const handleLearningProgressChange = useCallback(
    () => setHasUnreadLearning(hasUnreadLearningContent(user?.userId)),
    [user?.userId]
  );

  useEffect(() => {
    setHasUnreadLearning(hasUnreadLearningContent(user?.userId));
  }, [user?.userId]);

  useEffect(() => {
    if (paramLanguage) i18n.changeLanguage(paramLanguage);
  }, [i18n, paramLanguage]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const handlePreferencesChange = () => {
      setDatetimePreferencesVersion((previous) => previous + 1);
    };

    window.addEventListener(DATETIME_PREFERENCES_EVENT, handlePreferencesChange);

    return () => {
      window.removeEventListener(DATETIME_PREFERENCES_EVENT, handlePreferencesChange);
    };
  }, []);

  const LoginCta = () => (
    <Stack gap="1rem" alignItems="center" justifyContent="center" height="100%">
      <Logo variant="logoGrey" width="250px" />
      <Button onClick={login}>{translate(t.logIn)}</Button>
    </Stack>
  );

  const shouldDeferProtectedApp = authenticated && isBootstrapping;

  const protectedElement = (element: ReactNode) => {
    if (isAuthenticating || shouldDeferProtectedApp) return null;
    if (shouldShowLoginCta) return <LoginCta />;
    if (!authenticated) return null;
    return element;
  };

  return (
    <HeaderProvider>
      <Stack minHeight="100dvh" sx={{ '--app-content-padding-inline': contentPx }}>
        {urlToken && (
          <Suspense fallback={null}>
            <Routes>
              <Route path="report-pdf" element={<ReportPdf />} />
              <Route path="report-print" element={<ReportPrint />} />
              <Route path="installation-plan-pdf" element={<InstallationPlanPdf />} />
            </Routes>
          </Suspense>
        )}

        {!urlToken && (
          <>
            {runtimeConfig.maintenanceBannerEnabled && <MaintenanceBanner />}
            {!shouldDeferProtectedApp && (
              <NavBar
                hasUnreadLearningContent={hasUnreadLearning}
                onLearningCenterOpen={
                  authenticated ? () => setIsLearningCenterOpen(true) : undefined
                }
              />
            )}
            {authenticated && !shouldDeferProtectedApp && (
              <LearningCenter
                onClose={() => setIsLearningCenterOpen(false)}
                onProgressChange={handleLearningProgressChange}
                open={isLearningCenterOpen}
                userId={user?.userId}
              />
            )}
            <Stack component="main" id="main" p="2rem" px={contentPx} flex={1} minWidth={0}>
              {!shouldDeferProtectedApp && (
                <Suspense fallback={null}>
                  <AppRoutes protectedElement={protectedElement} paramLanguage={paramLanguage} />
                </Suspense>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </HeaderProvider>
  );
}
